import * as LocalAuthentication from 'expo-local-authentication';
import { Platform } from 'react-native';
import { secureStorage } from './secureStorage';

const BIOMETRIC_ENABLED_KEY = 'biometric_enabled';
const BIOMETRIC_PHONE_KEY = 'biometric_phone';

export interface BiometricCapability {
  available: boolean;
  label: string;
}

const unavailable: BiometricCapability = {
  available: false,
  label: 'Biometric',
};

export const biometricService = {
  async getCapability(): Promise<BiometricCapability> {
    if (Platform.OS === 'web') return unavailable;

    try {
      const [hasHardware, isEnrolled, supportedTypes] = await Promise.all([
        LocalAuthentication.hasHardwareAsync(),
        LocalAuthentication.isEnrolledAsync(),
        LocalAuthentication.supportedAuthenticationTypesAsync(),
      ]);

      if (!hasHardware || !isEnrolled) return unavailable;

      if (supportedTypes.includes(LocalAuthentication.AuthenticationType.FACIAL_RECOGNITION)) {
        return { available: true, label: 'Face unlock' };
      }

      if (supportedTypes.includes(LocalAuthentication.AuthenticationType.FINGERPRINT)) {
        return { available: true, label: 'Fingerprint' };
      }

      return { available: true, label: 'Biometric' };
    } catch {
      return unavailable;
    }
  },

  async isEnabled(): Promise<boolean> {
    return (await secureStorage.getItem(BIOMETRIC_ENABLED_KEY)) === 'true';
  },

  async getSavedPhone(): Promise<string | null> {
    return secureStorage.getItem(BIOMETRIC_PHONE_KEY);
  },

  async enableAfterPasswordLogin(phone: string): Promise<boolean> {
    const capability = await this.getCapability();
    if (!capability.available) return false;

    if (await this.isEnabled()) {
      await secureStorage.setItem(BIOMETRIC_PHONE_KEY, phone);
      return true;
    }

    const result = await LocalAuthentication.authenticateAsync({
      promptMessage: 'Enable biometric sign-in',
      cancelLabel: 'Not now',
      fallbackLabel: 'Use device passcode',
      disableDeviceFallback: false,
    });

    if (!result.success) return false;

    await secureStorage.setItem(BIOMETRIC_ENABLED_KEY, 'true');
    await secureStorage.setItem(BIOMETRIC_PHONE_KEY, phone);
    return true;
  },

  async authenticate(promptMessage = 'Sign in to Survey Connect'): Promise<boolean> {
    const capability = await this.getCapability();
    if (!capability.available) return false;

    const result = await LocalAuthentication.authenticateAsync({
      promptMessage,
      cancelLabel: 'Use password',
      fallbackLabel: 'Use device passcode',
      disableDeviceFallback: false,
    });

    return result.success;
  },

  async disable(): Promise<void> {
    await Promise.all([
      secureStorage.removeItem(BIOMETRIC_ENABLED_KEY),
      secureStorage.removeItem(BIOMETRIC_PHONE_KEY),
    ]);
  },
};
